'use client';

import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useEffect, useState } from 'react';
import { BlackHoleLoader } from './BlackHoleLoader';

// Constants for magic numbers
const ROTATION_RANGE = 30;
const MOUSE_RANGE = 100;
const PROGRESS_LIMIT = 95;
const PROGRESS_INCREMENT = 15;
const MESSAGE_INTERVAL = 1500;
const PROGRESS_INTERVAL = 500;
const PARTICLE_COUNT = 20;

interface Particle {
	id: string;
	x: number;
	y: number;
}

const loadingMessages = [
	'Initializing quantum systems...',
	'Calibrating neural networks...',
	'Loading matrix protocols...',
	'Establishing cyber connection...',
	'Preparing digital realm...',
	'Optimizing performance...',
	'Configuring security protocols...',
	'Establishing secure connection...',
	'Loading experience modules...',
	'Preparing your journey...',
];

export function PageLoader() {
	const [messageIndex, setMessageIndex] = useState(0);
	const [progress, setProgress] = useState(0);
	const [particles, setParticles] = useState<Array<Particle>>([]);
	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);

	// Initialize particles on client-side only
	useEffect(() => {
		setParticles(
			[...Array(PARTICLE_COUNT)].map(() => ({
				id: Math.random().toString(),
				x: Math.random() * window.innerWidth,
				y: Math.random() * window.innerHeight,
			}))
		);
	}, []);

	// Smooth spring animation for mouse movement
	const springConfig = { damping: 25, stiffness: 700 };
	const springX = useSpring(mouseX, springConfig);
	const springY = useSpring(mouseY, springConfig);

	// Transform mouse position into rotation
	const rotateX = useTransform(
		springY,
		[-MOUSE_RANGE, MOUSE_RANGE],
		[ROTATION_RANGE, -ROTATION_RANGE]
	);
	const rotateY = useTransform(
		springX,
		[-MOUSE_RANGE, MOUSE_RANGE],
		[-ROTATION_RANGE, ROTATION_RANGE]
	);

	// Handle mouse movement
	const handleMouseMove = (event: React.MouseEvent) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;
		mouseX.set(event.clientX - centerX);
		mouseY.set(event.clientY - centerY);
	};

	// Handle mouse leave
	const handleMouseLeave = () => {
		mouseX.set(0);
		mouseY.set(0);
	};

	// Cycle through loading messages and update progress
	useEffect(() => {
		const messageInterval = setInterval(() => {
			setMessageIndex(prev => (prev + 1) % loadingMessages.length);
		}, MESSAGE_INTERVAL);

		const progressInterval = setInterval(() => {
			setProgress(prev => {
				if (prev >= PROGRESS_LIMIT) return prev;
				return prev + Math.random() * PROGRESS_INCREMENT;
			});
		}, PROGRESS_INTERVAL);

		return () => {
			clearInterval(messageInterval);
			clearInterval(progressInterval);
		};
	}, []);

	return (
		<div
			className="bg-dark fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}>
			{/* Animated background grid */}
			<div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e5_1px,transparent_1px),linear-gradient(to_bottom,#4f46e5_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />

			{/* Glowing orbs */}
			<div className="absolute -left-4 top-1/4 h-72 w-72 rounded-full bg-purple-500/20 blur-[128px]" />
			<div className="absolute -right-4 bottom-1/4 h-72 w-72 rounded-full bg-blue-500/20 blur-[128px]" />

			<motion.div
				style={{
					rotateX,
					rotateY,
					transformStyle: 'preserve-3d',
				}}
				className="relative z-10 flex flex-col items-center">
				<BlackHoleLoader size="lg" className="mb-8" />
				<motion.p
					key={messageIndex}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -20 }}
					className="mb-4 font-mono text-lg text-matrix-400">
					{loadingMessages[messageIndex]}
				</motion.p>

				{/* Progress bar */}
				<div className="bg-cyber-darker h-1 w-64 overflow-hidden rounded-full">
					<motion.div
						className="h-full bg-matrix-400"
						initial={{ width: 0 }}
						animate={{ width: `${progress}%` }}
						transition={{ duration: 0.5, ease: 'easeOut' }}
					/>
				</div>
			</motion.div>

			{/* Interactive particles */}
			{particles.map((particle) => (
				<motion.div
					key={`particle-${particle.id}`}
					className="absolute h-1 w-1 rounded-full bg-matrix-400/50"
					initial={{
						x: 0,
						y: 0,
					}}
					animate={{
						x: [particle.x, Math.random() * window.innerWidth],
						y: [particle.y, Math.random() * window.innerHeight],
					}}
					transition={{
						duration: 3 + Math.random() * 2,
						repeat: Infinity,
						ease: 'linear',
						delay: Math.random() * 2,
					}}
				/>
			))}
		</div>
	);
}
